import { Injectable } from '@angular/core';

import CodeTool from '@editorjs/code';
import Delimiter from '@editorjs/delimiter';
import EditorJS from '@editorjs/editorjs';
import Embed from '@editorjs/embed';
import Header from '@editorjs/header';
import ImageTool from '@editorjs/image';
import InlineCode from '@editorjs/inline-code';
import List from '@editorjs/list';
import Marker from '@editorjs/marker';
import Paragraph from '@editorjs/paragraph';
import Table from '@editorjs/table';
import Underline from '@editorjs/underline';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EditorService {
  private editor: EditorJS | null = null;
  // Get API base URL from environment
  private apiBaseUrl = environment.apiUrl;

  /**
   * Инициализирует EditorJS с заданными настройками
   * @param holderId ID контейнера для редактора
   * @param data Предварительно заданные данные редактора
   * @param readOnly Режим только для чтения
   * @param placeholder Плейсхолдер для пустого редактора
   * @param imageUploadCallback Функция для загрузки изображений
   * @param onChangeCallback Функция обратного вызова при изменении редактора
   */
  initialize(
    holderId: string,
    data: any = {},
    readOnly: boolean = false,
    placeholder: string = 'Добавьте описание задачи...',
    imageUploadCallback?: (file: File) => Promise<{ success: boolean; file: { url: string } }>,
    onChangeCallback?: () => void
  ): EditorJS {
    // Не будем пытаться уничтожить предыдущий инстанс редактора в сервисе
    // EditorComponent сам отвечает за жизненный цикл своего экземпляра

    const editor = new EditorJS({
      holder: holderId,
      data: data,
      readOnly: readOnly,
      placeholder: placeholder,
      tools: {
        embed: {
          class: Embed,
          inlineToolbar: true,
          config: {
            services: {
              youtube: true,
              coub: true,
              codepen: true,
              vimeo: true,
              twitter: true,
              instagram: true,
              twitch: true,
            },
          },
        },
        header: {
          class: Header,
          config: {
            placeholder: 'Заголовок',
            levels: [2, 3, 4],
            defaultLevel: 2,
          },
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true,
        },
        list: {
          class: List,
          inlineToolbar: true,
          config: {
            defaultStyle: 'unordered',
          },
        },
        // checklist: {
        //   class: Checklist,
        //   inlineToolbar: true,
        // },
        // quote: {
        //   class: Quote,
        //   inlineToolbar: true,
        //   config: {
        //     quotePlaceholder: 'Введите цитату',
        //     captionPlaceholder: 'Автор цитаты',
        //   },
        // },
        code: CodeTool,
        image: {
          class: ImageTool,
          config: {
            uploader: {
              uploadByFile: imageUploadCallback
                ? imageUploadCallback
                : async (file: File) => {
                    // Заглушка для загрузки изображений, если колбэк не предоставлен
                    alert('Загрузка изображений не настроена');
                    return {
                      success: false,
                      file: {
                        url: '',
                      },
                    };
                  },
            },
          },
        },
        delimiter: Delimiter,
        table: Table,
        marker: {
          class: Marker,
          shortcut: 'CMD+SHIFT+M',
        },
        inlineCode: {
          class: InlineCode,
          shortcut: 'CMD+E',
        },
        underline: Underline,
      },
      onReady: () => {
        // Добавляем обработчик двойного клика для ссылок
        this.setupLinkDoubleClickHandler(holderId);
      },
      onChange: () => {
        // Вызываем переданный колбэк при изменении содержимого
        if (onChangeCallback) {
          onChangeCallback();
        }
      },
    });

    return editor;
  }

  /**
   * Настраивает обработчик двойного клика для ссылок
   * @param holderId ID контейнера редактора
   */
  private setupLinkDoubleClickHandler(holderId: string): void {
    const editorElement = document.getElementById(holderId);
    if (!editorElement) {
      return;
    }

    // Функция для добавления подсказок к ссылкам
    const addHintsToLinks = () => {
      const links = editorElement.querySelectorAll('a');
      links.forEach(link => {
        if (!link.getAttribute('title')) {
          link.setAttribute('title', 'Нажми два раза для открытия');
        }
      });
    };

    // Добавляем подсказки при инициализации
    addHintsToLinks();

    // Наблюдатель за изменениями в DOM для добавления подсказок к новым ссылкам
    const observer = new MutationObserver(() => {
      addHintsToLinks();
    });

    // Настраиваем наблюдатель для отслеживания изменений в дереве DOM
    observer.observe(editorElement, {
      childList: true,
      subtree: true,
    });

    editorElement.addEventListener('dblclick', event => {
      const target = event.target as HTMLElement;
      // Проверяем, является ли элемент ссылкой или находится внутри ссылки
      const linkElement = target.closest('a');

      if (linkElement) {
        event.preventDefault();
        const url = linkElement.getAttribute('href');
        if (url) {
          window.open(url, '_blank');
        }
      }
    });
  }

  /**
   * Сохраняет содержимое редактора
   * @param editor Экземпляр редактора EditorJS (опционально)
   * @returns Promise с данными редактора
   */
  async save(editor?: EditorJS): Promise<any> {
    if (!editor) {
      throw new Error('Редактор не инициализирован');
    }

    try {
      const savedData = await editor.save();
      return savedData;
    } catch (error) {
      console.error('Ошибка при сохранении данных редактора:', error);
      throw error;
    }
  }
}
